/**
 * TemplateMarketplace Page
 *
 * Browse and use pre-built agent templates from the marketplace.
 */
import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { JsonEditor } from '../components/ui/json-editor';
import {
  useTemplates,
  useCategoryCounts,
  useUseTemplate,
  useRateTemplate,
  useForkTemplate,
} from '../hooks/useTemplates';
import {
  Template,
  TemplateCategory,
  TEMPLATE_CATEGORIES,
  getCategoryInfo,
} from '../types/template';

/**
 * Star Rating Component
 */
interface StarRatingProps {
  rating: number;
  onRate?: (rating: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md';
}

function StarRating({ rating, onRate, readonly = true, size = 'sm' }: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const starSize = size === 'sm' ? 'text-sm' : 'text-lg';

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onRate?.(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className={`${starSize} ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          {star <= (hoverRating || rating) ? '⭐' : '☆'}
        </button>
      ))}
    </div>
  );
}

/**
 * Template Preview Modal
 */
interface TemplatePreviewModalProps {
  template: Template;
  onClose: () => void;
  onUse: (name?: string) => void;
  isUsing: boolean;
}

function TemplatePreviewModal({
  template,
  onClose,
  onUse,
  isUsing,
}: TemplatePreviewModalProps) {
  const [customName, setCustomName] = useState('');
  const rateTemplate = useRateTemplate();
  const forkTemplate = useForkTemplate();
  const categoryInfo = getCategoryInfo(template.category);

  const handleUse = () => {
    onUse(customName.trim() || undefined);
  };

  const handleRate = (rating: number) => {
    rateTemplate.mutate({ templateId: template.id, rating });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-14 h-14 shrink-0 bg-slate-100 rounded-xl flex items-center justify-center text-3xl">
              {template.icon || categoryInfo.icon}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold break-words">{template.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">
                  {categoryInfo.label}
                </span>
                {template.isOfficial && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                    Official
                  </span>
                )}
                <span className="text-xs text-muted-foreground">v{template.version}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Description</h3>
            <p className="text-slate-700">{template.description}</p>
          </div>

          {/* Forked from attribution */}
          {template.forkedFromId && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <span>Forked from another template</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900">{template.usageCount.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Times Used</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <div className="flex justify-center mb-1">
                <StarRating rating={Math.round(template.rating ?? 0)} size="md" />
              </div>
              <p className="text-xs text-muted-foreground">
                {(template.rating ?? 0).toFixed(1)} ({template.ratingCount ?? 0} reviews)
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900">{(template.forkCount ?? 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Forks</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl">{categoryInfo.icon}</p>
              <p className="text-xs text-muted-foreground">{categoryInfo.label}</p>
            </div>
          </div>

          {/* Tags */}
          {(template.tags ?? []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {(template.tags ?? []).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Author */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Author</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                {template.author?.avatar ? (
                  <img
                    src={template.author.avatar}
                    alt={template.author.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <span className="text-lg">👤</span>
                )}
              </div>
              <div>
                <p className="font-medium">{template.author?.name ?? 'Unknown'}</p>
                {template.isOfficial && (
                  <p className="text-xs text-blue-600">Verified Publisher</p>
                )}
              </div>
            </div>
          </div>

          {/* Configuration Preview */}
          {Object.keys(template.configuration).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                Configuration Preview
              </h3>
              <JsonEditor
                value={JSON.stringify(template.configuration, null, 2)}
                onChange={() => {}}
                disabled
                height={200}
              />
            </div>
          )}

          {/* Rate this template */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Rate this template</h3>
            <StarRating
              rating={0}
              onRate={handleRate}
              readonly={false}
              size="md"
            />
          </div>

          {/* Custom Name */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">
              Agent Name (optional)
            </h3>
            <Input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={`My ${template.name}`}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty to use the template name
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Created {new Date(template.createdAt).toLocaleDateString()}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => forkTemplate.mutate({ templateId: template.id })}
              disabled={forkTemplate.isPending}
            >
              {forkTemplate.isPending ? 'Forking...' : 'Fork'}
            </Button>
            <Button onClick={handleUse} disabled={isUsing}>
              {isUsing ? 'Creating Agent...' : 'Use Template'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Template Card Component
 */
interface TemplateCardProps {
  template: Template;
  onPreview: () => void;
}

function TemplateCard({ template, onPreview }: TemplateCardProps) {
  const categoryInfo = getCategoryInfo(template.category);

  return (
    <Card
      className="hover:shadow-lg transition-all cursor-pointer group"
      onClick={onPreview}
    >
      <CardHeader className="pb-3">
        {/* Every level between the card and the truncated title needs min-w-0.
            A flex item defaults to min-width:auto, so without it the row grows
            to fit the full name and a long title overflows the card instead of
            truncating ("Job Scout - ATS Board Watcher" ran past the edge). */}
        <div className="flex items-start justify-between min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
              {template.icon || categoryInfo.icon}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate" title={template.name}>
                {template.name}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1 min-w-0">
                <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 truncate">
                  {categoryInfo.label}
                </span>
                {template.isOfficial && (
                  <span className="text-xs text-blue-600 shrink-0">✓ Official</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="line-clamp-2 mb-3">
          {template.description}
        </CardDescription>

        {/* Stats Row */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            <StarRating rating={Math.round(template.rating ?? 0)} />
            <span className="text-muted-foreground ml-1">
              {(template.rating ?? 0).toFixed(1)}
            </span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span>📥</span>
            <span>{template.usageCount.toLocaleString()}</span>
          </div>
        </div>

        {/* Tags */}
        {(template.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {(template.tags ?? []).slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded-full bg-slate-50 text-slate-600"
              >
                {tag}
              </span>
            ))}
            {(template.tags ?? []).length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-slate-50 text-slate-600">
                +{(template.tags ?? []).length - 3}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Category Sidebar Component
 */
interface CategorySidebarProps {
  selectedCategory: TemplateCategory | null;
  onSelectCategory: (category: TemplateCategory | null) => void;
  categoryCounts: Record<string, number> | undefined;
  isLoading: boolean;
}

function CategorySidebar({
  selectedCategory,
  onSelectCategory,
  categoryCounts,
  isLoading,
}: CategorySidebarProps) {
  const totalCount = categoryCounts
    ? Object.values(categoryCounts).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="w-64 bg-white border-r border-slate-200 overflow-y-auto">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">Categories</h2>

        {/* All Templates */}
        <button
          onClick={() => onSelectCategory(null)}
          className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors mb-2 ${
            selectedCategory === null
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">🏪</span>
            <span className="font-medium">All Templates</span>
          </div>
          {isLoading ? (
            <LoadingSkeleton className="h-5 w-8" />
          ) : (
            <span className={`text-sm ${selectedCategory === null ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
              {totalCount}
            </span>
          )}
        </button>

        <div className="h-px bg-slate-200 my-3" />

        {/* Category List */}
        <div className="space-y-1">
          {TEMPLATE_CATEGORIES.map((category) => {
            const count = categoryCounts?.[category.value] || 0;
            const isSelected = selectedCategory === category.value;

            return (
              <button
                key={category.value}
                onClick={() => onSelectCategory(category.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{category.icon}</span>
                  <span className="font-medium text-sm">{category.label}</span>
                </div>
                {isLoading ? (
                  <LoadingSkeleton className="h-5 w-6" />
                ) : (
                  <span className={`text-sm ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * TemplateMarketplace Page Component
 */
export default function TemplateMarketplace() {
  const user = useAuthStore((state) => state.user);
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'usageCount' | 'rating' | 'createdAt'>('usageCount');
  const [page, setPage] = useState(1);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const { data, isLoading, error } = useTemplates({
    page,
    limit: 12,
    category: selectedCategory || undefined,
    search: searchQuery || undefined,
    sortBy,
    sortOrder: 'desc',
  });

  const { data: categoryCounts, isLoading: isLoadingCounts } = useCategoryCounts();
  const useTemplate = useUseTemplate();

  const templates = data?.data || [];
  const meta = data?.meta;

  const handleUseTemplate = (name?: string) => {
    if (previewTemplate) {
      useTemplate.mutate(
        { templateId: previewTemplate.id, name },
        { onSuccess: () => setPreviewTemplate(null) }
      );
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Navigation Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/agents"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Agents
              </Link>
              <Link
                to="/workflows"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Workflows
              </Link>
              <Link
                to="/executions"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Executions
              </Link>
              <Link
                to="/templates"
                className="text-sm font-medium text-foreground"
              >
                Marketplace
              </Link>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Page Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Template Marketplace</h1>
              <p className="text-muted-foreground">
                Browse and use pre-built agent templates
              </p>
            </div>
            {user && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/templates/analytics">My Analytics</Link>
              </Button>
            )}
          </div>

          {/* Search and Sort */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search templates..."
                className="pl-10"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="usageCount">Most Used</option>
              <option value="rating">Highest Rated</option>
              <option value="createdAt">Newest</option>
            </select>
            {(searchQuery || selectedCategory) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Category Sidebar */}
        <CategorySidebar
          selectedCategory={selectedCategory}
          onSelectCategory={(cat) => {
            setSelectedCategory(cat);
            setPage(1);
          }}
          categoryCounts={categoryCounts}
          isLoading={isLoadingCounts}
        />

        {/* Template Grid */}
        <main className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <LoadingSkeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1">
                        <LoadingSkeleton className="h-5 w-3/4 mb-2" />
                        <LoadingSkeleton className="h-4 w-1/2" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <LoadingSkeleton className="h-12 w-full mb-3" />
                    <LoadingSkeleton className="h-4 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="text-6xl mb-4">❌</div>
                <CardTitle className="mb-2">Error Loading Templates</CardTitle>
                <CardDescription>
                  {error instanceof Error ? error.message : 'An error occurred'}
                </CardDescription>
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : templates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <CardTitle className="mb-2">No Templates Found</CardTitle>
                <CardDescription>
                  {searchQuery || selectedCategory
                    ? 'No templates match your filters. Try adjusting your search.'
                    : 'No templates available yet.'}
                </CardDescription>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Results info */}
              <div className="mb-4 text-sm text-muted-foreground">
                Showing {templates.length} of {meta?.total || 0} templates
                {selectedCategory && (
                  <span> in {getCategoryInfo(selectedCategory).label}</span>
                )}
              </div>

              {/* Template Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onPreview={() => setPreviewTemplate(template)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {meta && meta.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of {meta.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                    disabled={page === meta.pages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUse={handleUseTemplate}
          isUsing={useTemplate.isPending}
        />
      )}
    </div>
  );
}
